import { Divider } from '@mui/material';
import {
  GridRenderEditCellParams,
  useGridApiContext,
} from '@mui/x-data-grid-premium';
import {
  ChangeCategoryRequest,
  ChangeCategoryResult,
  MLCategory,
} from 'api/ml-service/ml-service.types';
import AutocompleteBase from 'components/forms/fields/autocomplete-field/autocomplete-base/autocomplete-base.component';
import { useCallback, useEffect, useState } from 'react';

type SubCategoryOption = {
  id: string;
  label: string;
  category: MLCategory;
};

const subCategoryOptions: SubCategoryOption[] = [
  {
    id: 'baked goods',
    label: 'BAKED GOODS',
    category: 'main',
  },
  {
    id: 'entrees',
    label: 'ENTREES',
    category: 'main',
  },
  {
    id: 'pizza',
    label: 'PIZZA',
    category: 'main',
  },
  {
    id: 'sandwiches',
    label: 'SANDWICHES',
    category: 'main',
  },
  {
    id: 'burgers',
    label: 'BURGERS',
    category: 'main',
  },
  {
    id: 'soup',
    label: 'SOUP',
    category: 'main',
  },
  {
    id: 'salads',
    label: 'SALADS',
    category: 'main',
  },
  {
    id: 'side',
    label: 'SIDE',
    category: 'side',
  },
  {
    id: 'modifier',
    label: 'MODIFIER',
    category: 'modifier',
  },
  {
    id: 'dessert',
    label: 'DESSERT',
    category: 'dessert',
  },
  {
    id: 'beverage',
    label: 'BEVERAGE',
    category: 'beverage',
  },
  {
    id: 'non food',
    label: 'NON FOOD',
    category: 'non food',
  },
];

const ItemClassifierGridAutocomplete = (
  props: GridRenderEditCellParams & {
    changeCategory: (
      payload: ChangeCategoryRequest,
    ) => Promise<{ data: ChangeCategoryResult } | { error: unknown }>;
  },
) => {
  const { id, field, value, row, changeCategory } = props;
  const apiRef = useGridApiContext();

  const handleChange = useCallback(
    async (_, newValue, _reason) => {
      try {
        const res = await changeCategory({
          itemName: row.itemName,
          category: newValue.category,
          subCategory: newValue.id,
          previousSubCategory: row.subCategory,
          previousCategory: row.category,
        });
        if ('data' in res && res.data.itemName) {
          await apiRef.current.setEditCellValue({
            id,
            field,
            value: newValue.label,
          });
        }
        await apiRef.current.stopCellEditMode({
          id,
          field,
        });
      } catch (e) {}
    },
    [apiRef, changeCategory, field, id, row],
  );

  const [open, setOpen] = useState(false);

  useEffect(() => {
    const t = setTimeout(() => setOpen(true), 0);
    return () => clearTimeout(t);
  }, []);

  return (
    <AutocompleteBase
      options={subCategoryOptions}
      open={open}
      onClose={() => setOpen(false)}
      value={value?.toLowerCase()}
      data-testid="ItemClassifierGridAutocomplete.AutocompleteBase"
      onChange={async (...args) => {
        await handleChange(...args);
      }}
      disableClearable
      groupBy={option => option.category}
      renderGroup={params => {
        return (
          <li key={params.key}>
            {params.children}
            <Divider />
          </li>
        );
      }}
      TextFieldProps={{
        sx: { verticalAlign: 'middle', margin: 'auto' },
      }}
      disablePortal={false}
    />
  );
};

export default ItemClassifierGridAutocomplete;
